/**
 * The one definition of URL identity in the app.
 *
 * It lives here rather than in the transport because two canonicalisations — one
 * harvesting the keys, one matching against them — is exactly how the
 * citation-honesty gate leaks: either a URL the search genuinely returned gets
 * dropped, or one it never returned gets admitted.
 *
 * Deliberately minimal. An over-eager canonicaliser that stripped query strings
 * would make `example.com/?p=99` and `example.com/?p=1` compare equal, and a
 * great many papers are addressed by `?doi=`.
 */
export function canonicalUrl(url: string): string {
  try {
    const u = new URL(url.trim())
    u.hash = ''
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '')
    return `${u.protocol.toLowerCase()}//${u.host.toLowerCase()}${path}${u.search}`
  } catch {
    // Not a URL at all. Returned rather than thrown so the caller's gate sees a
    // key that can never match a harvested one, which is the correct outcome.
    return url.trim().toLowerCase()
  }
}

/** Only http(s) reaches the inspector as a link. Anything else is not a source. */
export function isHttpUrl(url: string): boolean {
  try {
    return /^https?:$/.test(new URL(url.trim()).protocol)
  } catch {
    return false
  }
}

/** The host, for a source row that has to stay on one line. */
export function hostOf(url: string): string {
  try {
    return new URL(url.trim()).host.replace(/^www\./, '')
  } catch {
    return url.trim().slice(0, 40)
  }
}

/**
 * A bare DOI addressed by this URL, lowercased, or nothing.
 *
 * One work commonly exists as a preprint, a publisher landing page, a `doi.org`
 * resolver, a PubMed Central copy and an institutional mirror — five registrable
 * domains, one artefact by one author team, every copy carrying the same quotable
 * text. `registrableHost` reads that as five independent owners, and it is
 * exactly wrong: **one work on five hosts is one source.** This is the identifier
 * saying so outright, which is the cheapest and least arguable of the three
 * collapse keys in `similarity.ts`'s company.
 *
 * The path AND the query, because `canonicalUrl` above already records that a
 * great many papers are addressed by `?doi=` — reading the path alone would miss
 * the case that motivated keeping query strings in the first place.
 *
 * Deliberately not a DOI validator. It answers "do these two URLs address the
 * same work", so a string that is not a real DOI costs nothing as long as two
 * URLs carrying it agree, and the shape is narrow enough (`10.` plus a registrant
 * of 4–9 digits) that ordinary paths do not collide with it.
 */
const DOI = /10\.\d{4,9}\/[^\s"'<>#?&]+/

export function doiOf(url: string): string | undefined {
  let u: URL
  try {
    u = new URL(url.trim())
  } catch {
    return undefined
  }
  for (const part of [u.pathname, u.search]) {
    let decoded = part
    try {
      // `?doi=10.1234%2Fabc` is the same address as `?doi=10.1234/abc`, and the
      // escaped form is the common one. A malformed escape is left as it was
      // rather than throwing the whole lookup away.
      decoded = decodeURIComponent(part)
    } catch {
      /* not decodable — match against the raw form */
    }
    const m = DOI.exec(decoded)
    // A trailing slash is the same address, spelled with one — the same rule
    // `canonicalUrl` applies to a path.
    if (m) return m[0].replace(/\/+$/, '').toLowerCase()
  }
  return undefined
}

/**
 * The last two labels of a host, as a cheap stand-in for the registrable domain.
 *
 * Deliberately NOT a Public Suffix List. This is used to ask whether two sources
 * are INDEPENDENT, and the two errors are not equally bad: over-merging (calling
 * two hosts one domain) costs a true refutation its loud channel, which the reader
 * can see and act on, while under-merging (calling one attacker's two hosts
 * independent) hands them the most destructive reading in the app. Every ambiguous
 * case here therefore resolves toward merging.
 *
 * That asymmetry is also why the suffix list below can afford to be incomplete. A
 * suffix that is MISSING makes two sites under it reduce to the same string —
 * `a.co.kr` and `b.co.kr` both become `co.kr` — which merges them. Wrong, but wrong
 * in the safe direction. A short list fails conservatively and does not have to be
 * maintained against the real PSL.
 */
const MULTI_LABEL_SUFFIX = new Set([
  'co.uk',
  'ac.uk',
  'gov.uk',
  'org.uk',
  'co.jp',
  'ac.jp',
  'com.au',
  'edu.au',
  'gov.au',
  'co.nz',
  'com.br',
  'co.in',
  'ac.in',
  'com.cn',
  'edu.cn',
  'co.za',
])

/** Dotted-quad. Reduced by labels it would yield a nonsense "domain" of the last
 *  two octets, merging `1.2.3.4` with `5.6.3.4` — two unrelated hosts, in the
 *  direction that loses a real refutation. */
const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/

export function registrableHost(url: string): string {
  const h = hostOf(url)
    // `URL.host` keeps the port, and a port is not part of a domain — otherwise
    // `example.com:8443` and `example.com` would read as two independent sources.
    // An IPv6 literal's colons live inside brackets, so strip only a trailing one.
    .replace(/^(\[[^\]]*\])(?::\d+)?$/, '$1')
    .replace(/:\d+$/, '')
    // A trailing dot is the same host, spelled absolutely.
    .replace(/\.$/, '')
    .toLowerCase()

  // An address is one source whatever its shape, and has no labels to reduce.
  if (h.startsWith('[') || IPV4.test(h)) return h

  const parts = h.split('.')
  if (parts.length <= 2) return h
  const lastTwo = parts.slice(-2).join('.')
  return MULTI_LABEL_SUFFIX.has(lastTwo) ? parts.slice(-3).join('.') : lastTwo
}
